/**
 * useCredits — Hook quản lý credit balance cho commercial mode
 *
 * Khi DB chưa sẵn sàng: dùng mock data (services/mock/creditMock.ts)
 * Khi production: đổi sang Supabase queries (chỉ thay import)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { isCommercial } from '../config/appMode';
import { useAuth } from '../contexts/AuthContext';
import {
  getMockBalance,
  getMockTransactions,
  getMockPacks,
  mockCheckCredits,
  mockDeductCredits,
  mockAddCredits,
  mockPurchasePack,
  estimateTokensFromSegments,
  tokensToPages,
  formatCredits,
  formatVnd,
} from '../services/mock/creditMock';
import type { CreditBalance, CreditPack, CreditTransaction, CreditCheck, TokenEstimate } from '../types/credit';

// TODO: Khi có Supabase, thay mock imports bằng:
// import { supabase } from '../services/supabase/supabaseClient';

export function useCredits() {
  const { user, session, profile } = useAuth();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [phoneVerified, setPhoneVerified] = useState(false);

  // Không dùng credit system trong internal mode
  if (!isCommercial) {
    return {
      balance: null,
      transactions: [],
      packs: [],
      isLoading: false,
      phoneVerified: true,
      isLow: false,
      isEmpty: false,
      canAfford: () => true, // internal = unlimited
      checkCredits: () => ({ allowed: true, creditsNeeded: 0, creditsAvailable: Infinity, creditsAfter: Infinity }),
      deductCredits: () => {},
      purchasePack: () => {},
      estimateTokens: () => ({ inputTokens: 0, outputTokens: 0, totalTokens: 0, estimatedCredits: 0, equivalentPages: 0 }),
      verifyPhone: () => {},
      formatCredits,
      formatVnd,
      refetch: () => {},
    };
  }

  const fetchData = useCallback(async () => {
    if (!user) {
      setBalance(null);
      setTransactions([]);
      setIsLoading(false);
      return;
    }

    const workersUrl = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';
    const token = session?.access_token;

    // Balance: fetch từ API thật (authenticated)
    if (token) {
      try {
        const res = await fetch(`${workersUrl}/api/credits/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBalance({
            credits: data.credits ?? 0,
            creditsUsed: data.credits_used ?? 0,
            creditsPurchased: data.credits_purchased ?? 0,
          });
        } else {
          setBalance(getMockBalance());
        }
      } catch {
        setBalance(getMockBalance());
      }
    } else {
      setBalance(getMockBalance());
    }

    // Transactions: fetch từ API thật (paginated, giới hạn 50 gần nhất)
    if (token) {
      try {
        const res = await fetch(`${workersUrl}/api/credits/transactions?page=1&limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const mapped: CreditTransaction[] = (data.transactions || []).map((t: any) => ({
            id: t.id,
            userId: t.user_id,
            type: t.type,
            credits: t.credits,
            balanceAfter: t.balance_after,
            reason: t.reason ?? '',
            relatedPurchaseId: t.purchase_id ?? undefined,
            relatedJobId: t.job_id ?? undefined,
            createdAt: t.created_at,
          }));
          setTransactions(mapped);
        } else {
          setTransactions(getMockTransactions());
        }
      } catch {
        setTransactions(getMockTransactions());
      }
    } else {
      setTransactions(getMockTransactions());
    }

    // Packs: fetch từ API thật (public endpoint, không cần auth)
    try {
      const res = await fetch(`${workersUrl}/api/credits/packs`);
      if (res.ok) {
        const { packs: dbPacks } = await res.json();
        const basePrice = Math.min(...dbPacks.map((p: any) => p.price_vnd / (p.credits + (p.bonus_credits || 0))));
        const mapped: CreditPack[] = dbPacks.map((p: any, idx: number) => {
          const total = p.credits + (p.bonus_credits || 0);
          const pricePerCredit = p.price_vnd / total;
          return {
            id: p.id,
            name: p.name,
            credits: p.credits,
            bonusCredits: p.bonus_credits || 0,
            totalCredits: total,
            priceVnd: p.price_vnd,
            pricePerCredit,
            savingsPercent: Math.round((1 - pricePerCredit / basePrice) * 100),
            isPopular: idx === 2, // Gói Vừa = popular (display_order=3)
            description: '',
          };
        });
        setPacks(mapped);
      } else {
        // Fallback mock khi API fail
        setPacks(getMockPacks());
      }
    } catch {
      setPacks(getMockPacks());
    }

    setIsLoading(false);
  }, [user, session]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // TODO: Khi có Supabase, thêm Realtime subscription:
  // useEffect(() => {
  //   const channel = supabase.channel('credits').on('postgres_changes', { event: 'UPDATE', table: 'token_balance', filter: `user_id=eq.${user?.id}` }, () => fetchData()).subscribe();
  //   return () => { supabase.removeChannel(channel); };
  // }, [user, fetchData]);

  const isLow = (balance?.credits ?? 0) < 50_000;
  const isEmpty = (balance?.credits ?? 0) <= 0;

  const canAfford = useCallback((tokensNeeded: number): boolean => {
    return (balance?.credits ?? 0) >= tokensNeeded;
  }, [balance]);

  const checkCredits = useCallback((tokensNeeded: number): CreditCheck => {
    // Internal/admin role = exempt from billing (matches RPC charge_and_create_job logic:
    // v_role IS DISTINCT FROM 'user' → v_charged := 0). Show "đủ" so preflight không block.
    const isExempt = profile?.role === 'internal' || profile?.role === 'admin';
    if (isExempt) {
      return {
        allowed: true,
        creditsNeeded: 0,
        creditsAvailable: Infinity,
        creditsAfter: Infinity,
      };
    }
    const available = balance?.credits ?? 0;
    const allowed = available >= tokensNeeded;
    return {
      allowed,
      creditsNeeded: tokensNeeded,
      creditsAvailable: available,
      creditsAfter: allowed ? available - tokensNeeded : available,
      reason: allowed ? undefined : `Cần ${tokensNeeded.toLocaleString()} credits, chỉ còn ${available.toLocaleString()}`,
    };
  }, [balance, profile]);

  const deductCredits = useCallback((tokens: number, reason: string, jobId?: string) => {
    const newBalance = mockDeductCredits(tokens, reason, jobId);
    setBalance(newBalance);
    setTransactions(getMockTransactions());
  }, []);

  const purchasePack = useCallback((packId: string) => {
    const newBalance = mockPurchasePack(packId);
    if (newBalance) {
      setBalance(newBalance);
      setTransactions(getMockTransactions());
    }
  }, []);

  const estimateTokens = useCallback((segments: string[]): TokenEstimate => {
    const { inputTokens, outputTokens, totalTokens } = estimateTokensFromSegments(segments);
    return {
      inputTokens,
      outputTokens,
      totalTokens,
      estimatedCredits: totalTokens,
      equivalentPages: tokensToPages(totalTokens),
    };
  }, []);

  const verifyPhone = useCallback(() => {
    // Mock: add free credits
    const newBalance = mockAddCredits(200_000, 'welcome', 'Phone verified — 200K credits miễn phí (đủ 1 báo cáo dày)');
    setBalance(newBalance);
    setTransactions(getMockTransactions());
    setPhoneVerified(true);
  }, []);

  const suggestedPack = useMemo(() => {
    if (!balance) return packs.find(p => p.isPopular) || packs[0];
    // Suggest pack dựa trên current balance
    if (balance.credits < 100_000) return packs[0]; // Gói Thử
    if (balance.credits < 500_000) return packs[1]; // Gói Nhỏ
    return packs.find(p => p.isPopular) || packs[2]; // Gói Vừa
  }, [balance, packs]);

  return {
    balance,
    transactions,
    packs,
    isLoading,
    phoneVerified,
    isLow,
    isEmpty,
    canAfford,
    checkCredits,
    deductCredits,
    purchasePack,
    estimateTokens,
    verifyPhone,
    suggestedPack,
    formatCredits,
    formatVnd,
    refetch: fetchData,
  };
}
